import type {
  CardDefinitionId,
  CardInstanceId,
  CardInstanceRecord,
  EngineArmyCardDefinition,
  EngineArmyCardDefinitionV3,
  EngineCardDefinition,
  EngineCommanderCardDefinitionV3,
  EngineCommanderCardDefinitionV4,
  EngineEventCardDefinitionV4,
  EngineLandCardDefinition,
  EngineLandCardDefinitionV1,
  GameState,
  GameStateV3,
  GameStateV4,
  GameStateV5,
  SetupGameState,
} from "../types";

export function isSetupGameState(state: Readonly<GameState>): state is Readonly<SetupGameState> {
  return (
    state.schemaVersion === 2 ||
    state.schemaVersion === 3 ||
    state.schemaVersion === 4 ||
    state.schemaVersion === 5
  );
}

export function getCardInstance(
  state: Readonly<GameState>,
  cardInstanceId: CardInstanceId,
): CardInstanceRecord | undefined {
  return isSetupGameState(state) ? state.cardInstances[cardInstanceId] : undefined;
}

export function getCardDefinition(
  state: Readonly<GameState>,
  cardDefinitionId: CardDefinitionId,
): EngineCardDefinition | undefined {
  return isSetupGameState(state)
    ? state.catalog.cards.find((definition) => definition.id === cardDefinitionId)
    : undefined;
}

export function getCardInstanceDefinition(
  state: Readonly<GameState>,
  cardInstanceId: CardInstanceId,
): EngineCardDefinition | undefined {
  const instance = getCardInstance(state, cardInstanceId);
  return instance === undefined ? undefined : getCardDefinition(state, instance.cardDefinitionId);
}

export function getLandDefinitionForInstance(
  state: Readonly<GameState>,
  cardInstanceId: CardInstanceId,
): EngineLandCardDefinition | EngineLandCardDefinitionV1 | undefined {
  const definition = getCardInstanceDefinition(state, cardInstanceId);
  return definition?.type === "land" ? definition : undefined;
}

export function getArmyDefinitionForInstance(
  state: Readonly<GameState>,
  cardInstanceId: CardInstanceId,
): EngineArmyCardDefinition | undefined {
  if (state.schemaVersion !== 3 && state.schemaVersion !== 4 && state.schemaVersion !== 5) {
    return undefined;
  }
  const definition = getCardInstanceDefinition(state, cardInstanceId);
  return definition?.type === "army" && "upkeep" in definition ? definition : undefined;
}

export function getArmyDefinitionV3ForInstance(
  state: Readonly<GameState>,
  cardInstanceId: CardInstanceId,
): EngineArmyCardDefinitionV3 | undefined {
  if (
    (state.schemaVersion !== 3 && state.schemaVersion !== 4 && state.schemaVersion !== 5) ||
    (state.catalog.schemaVersion !== 3 && state.catalog.schemaVersion !== 4)
  ) {
    return undefined;
  }

  const definition = getCardInstanceDefinition(state, cardInstanceId);
  return definition?.type === "army" && "cost" in definition && "upkeep" in definition
    ? definition
    : undefined;
}

export function getCommanderDefinitionForInstance(
  state: Readonly<GameStateV3 | GameStateV4>,
  cardInstanceId: CardInstanceId,
): EngineCommanderCardDefinitionV3 | undefined;
export function getCommanderDefinitionForInstance(
  state: Readonly<GameStateV5>,
  cardInstanceId: CardInstanceId,
): EngineCommanderCardDefinitionV4 | undefined;
export function getCommanderDefinitionForInstance(
  state: Readonly<GameState>,
  cardInstanceId: CardInstanceId,
): EngineCommanderCardDefinitionV3 | EngineCommanderCardDefinitionV4 | undefined;
export function getCommanderDefinitionForInstance(
  state: Readonly<GameState>,
  cardInstanceId: CardInstanceId,
): EngineCommanderCardDefinitionV3 | EngineCommanderCardDefinitionV4 | undefined {
  if (
    (state.schemaVersion !== 3 && state.schemaVersion !== 4 && state.schemaVersion !== 5) ||
    (state.catalog.schemaVersion !== 3 && state.catalog.schemaVersion !== 4)
  ) {
    return undefined;
  }

  const definition = getCardInstanceDefinition(state, cardInstanceId);
  return definition?.type === "commander" && "cost" in definition ? definition : undefined;
}

export function getEventDefinitionForInstance(
  state: Readonly<GameState>,
  cardInstanceId: CardInstanceId,
): EngineEventCardDefinitionV4 | undefined {
  if (state.schemaVersion !== 5 || state.catalog.schemaVersion !== 4) return undefined;
  const definition = getCardInstanceDefinition(state, cardInstanceId);
  return definition?.type === "event" && "effects" in definition ? definition : undefined;
}
